import {CreatorRow as CreatorEntity} from "./creator-row"
import {GedcomLanguage as Language} from "../model/gedcom-language"
import {Relationships} from "../utils/csv-template-utils"
import {DateOrYear} from "../datepicker/date-or-year"
import {formatDateOrYear} from "../datepicker/custom-datepicker"
import {Locale} from "../locale"


export enum Sex { M = "M", F = "F" }

/**
 * GEDCOM extended.
 */
class Individual extends CreatorEntity {
    sex?: Sex
    givenName?: string
    // [PLACEHOLDER] full name pattern locale-specific columns
    birthDate?: DateOrYear | any
    birthPlace?: string
    deceaseDate?: DateOrYear | any
    deceasePlace?: string
    notes?: string
    // Parents
    fatherId?: string
    motherId?: string
    // GEDCOM extras
    image?: Blob
    ethnicity?: string
    languages: Set<Language> = new Set()
    // GEDCOM bindings
    numberOfChildren = 0
    numberOfMarriages = 0
    familyChild?: string
    familySpouses: Set<string> = new Set()

    constructor(params: { id: string, sex?: Sex, [key: string]: any }) {
        super(params)
        Object.assign(this, params)
    }

    withParent(): boolean {
        return !!(this.fatherId || this.motherId)
    }

    hasBothParents(): boolean {
        return !!(this.fatherId && this.motherId)
    }

    generation(_: Relationships): number | undefined {
        throw new Error("Method not implemented.")
    }
}

/**
 * For locale-specific full name pattern.
 */
export class I18nIndividual extends Individual {
    middleName?: string
    surname1?: string
    surname2?: string
    nickname?: string

    constructor(params: ConstructorParameters<typeof Individual>[0] & {
        middleName?: string
        surname1?: string
        surname2?: string
        nickname?: string
    }) {
        super(params)
        this.middleName = params.middleName
        this.surname1 = params.surname1
        this.surname2 = params.surname2
        this.nickname = params.nickname
    }

    static prefix = "I"
    static empty = (id: number): InstanceType<typeof this> => new this({ id: `${this.prefix}${id}` })

    static getFullName = (indi: Partial<I18nIndividual>, locale?: string, capitalizeSurname = false): string | undefined => {
        let parts: string[] = []
        switch (locale) {
            case Locale.ES: {
                // name middlename surname1 surname2 "nickname"
                if (indi.givenName) parts.push(indi.givenName)
                if (indi.middleName) parts.push(indi.middleName)
                if (indi.surname1) parts.push(capitalizeSurname ? `/${indi.surname1.toUpperCase()}/` : indi.surname1)
                if (indi.surname2) parts.push(indi.surname2)
                if (indi.nickname) parts.push(`"${indi.nickname}"`)
                break
            }
            case Locale.EN:
            case Locale.PL:
            default: {
                // name "nickname" middlename surname1
                if (indi.givenName) parts.push(indi.givenName)
                if (indi.nickname) parts.push(`"${indi.nickname}"`)
                if (indi.middleName) parts.push(indi.middleName)
                if (indi.surname1) parts.push(capitalizeSurname ? `/${indi.surname1.toUpperCase()}/` : indi.surname1)
                break
            }
        }
        return parts.filter(Boolean).join(" ") || undefined;
    }

    asGedcom(locale?: string): string {
        let gedcom = `0 @${this.id}@ INDI\n`
        if (!this.enabled) gedcom += `1 _ENABLED ${this.enabled}\n`

        const fullName = I18nIndividual.getFullName(this, locale, true)
        if (fullName) gedcom += `1 NAME ${fullName}\n`
        if (this.givenName) gedcom += `2 GIVN ${this.givenName}\n`
        if (this.surname1) gedcom += `2 SURN ${this.surname1}\n`
        if (this.nickname) gedcom += `2 NICK ${this.nickname}\n`
        gedcom += `1 SEX ${this.sex}\n`
        if (this.birthDate || this.birthPlace) {
            gedcom += `1 BIRT\n`
            if (this.birthDate) gedcom += `2 DATE ${formatDateOrYear(this.birthDate)}\n`
            if (this.birthPlace) gedcom += `2 PLAC ${this.birthPlace}\n`
        }
        if (this.deceaseDate || this.deceasePlace) {
            gedcom += `1 DEAT\n`
            if (this.deceaseDate) gedcom += `2 DATE ${formatDateOrYear(this.deceaseDate)}\n`
            if (this.deceasePlace) gedcom += `2 PLAC ${this.deceasePlace}\n`
        }
        if (this.ethnicity) gedcom += `1 _ETHN ${capitalize(this.ethnicity)}\n`
        for (const language of this.languages) {
            gedcom += `1 LANG ${capitalize(language.name.toLowerCase())}\n`
        }
        for (const famID of this.familySpouses) {
            gedcom += `1 FAMS @${famID}@\n`
        }
        if (this.familyChild) gedcom += `1 FAMC @${this.familyChild}@\n`
        if (this.notes) gedcom += `1 NOTE ${this.notes}\n`

        return gedcom
    }
}

// noinspection JSUnusedGlobalSymbols
export class IndividualBindings extends I18nIndividual {

    constructor(obj: any) {
        super({ id: obj.id })
        Object.assign(this, obj)
    }

    /**
     * Supplementary bindings for anti tampering checks after import.
     */
    _numberOfChildren?: number
    _numberOfMarriages?: number
    _familyChild?: string
    _familySpouses: Set<string> = new Set()
}

export function capitalize(str?: string): string | undefined {
    return str ? str
        .toLowerCase()
        .split(/[-\s]/)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join((str.includes('-') ? '-' : ' ')) : undefined
}
